
import env from '@/config/env';
import { ReportHandler } from '@/types/performance';

// Analytics provider types
type AnalyticsProvider = 'console' | 'google-analytics' | 'mixpanel' | 'custom';

// Configuration interface
interface AnalyticsConfig {
  provider: AnalyticsProvider;
  enabled: boolean;
  anonymizeIp?: boolean;
  trackErrors?: boolean;
  sampleRate?: number;
}

// Default configuration
const defaultConfig: AnalyticsConfig = {
  provider: env.analyticsProvider as AnalyticsProvider || 'console',
  enabled: env.analyticsEnabled,
  anonymizeIp: true,
  trackErrors: true,
  sampleRate: 1.0, // Track 100% of users by default
};

let analyticsConfig = { ...defaultConfig };

/**
 * Initialize analytics tracking
 * @param config Optional configuration for the analytics
 */
export function initAnalytics(config?: Partial<AnalyticsConfig>): void {
  // Merge provided config with defaults
  analyticsConfig = { ...defaultConfig, ...config };
  
  if (!analyticsConfig.enabled) {
    console.log('[Analytics] Analytics tracking is disabled');
    return;
  }

  // Only set up tracking in production unless forced
  if (!env.isProduction && !config?.enabled) {
    console.log('[Analytics] Analytics tracking is disabled in development');
    return;
  }

  // Initialize the appropriate analytics provider
  if (analyticsConfig.provider === 'google-analytics' && env.googleAnalyticsId) {
    initGoogleAnalytics(env.googleAnalyticsId);
  } else {
    console.log(`[Analytics] Initialized with provider: ${analyticsConfig.provider}`);
  }
  
  // Set up error tracking if enabled
  if (analyticsConfig.trackErrors) {
    setupErrorTracking();
  }
}

/**
 * Initialize Google Analytics
 */
function initGoogleAnalytics(measurementId: string): void {
  if (!measurementId) {
    console.error('[Analytics] Google Analytics measurement ID is required');
    return;
  }

  // Add Google Analytics script to the page
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  // Initialize the global gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag(...args: any[]) {
    window.dataLayer.push(arguments);
  }
  gtag('js', new Date());
  gtag('config', measurementId, {
    anonymize_ip: analyticsConfig.anonymizeIp,
    send_page_view: false // We'll handle page views manually
  });

  console.log('[Analytics] Google Analytics initialized');
}

/**
 * Track a page view event
 * @param path The page path
 * @param title Optional page title
 */
export function trackPageView(path: string, title?: string): void {
  if (!analyticsConfig.enabled) return;
  
  // Apply sampling if configured
  if (analyticsConfig.sampleRate !== undefined && analyticsConfig.sampleRate < 1.0) {
    if (Math.random() > analyticsConfig.sampleRate) {
      return;
    }
  }

  if (analyticsConfig.provider === 'console') {
    console.log(`[Analytics] Page View: ${path}${title ? ` - ${title}` : ''}`);
  } else if (analyticsConfig.provider === 'google-analytics' && typeof window !== 'undefined' && 'gtag' in window) {
    window.gtag('config', env.googleAnalyticsId!, {
      page_path: path,
      page_title: title,
      anonymize_ip: analyticsConfig.anonymizeIp
    });
  }
}

/**
 * Track a custom event
 * @param eventName The event name
 * @param properties Optional event properties
 */
export function trackEvent(eventName: string, properties?: Record<string, any>): void {
  if (!analyticsConfig.enabled) return;

  if (analyticsConfig.provider === 'console') {
    console.log(`[Analytics] Event: ${eventName}`, properties || {});
  } else if (analyticsConfig.provider === 'google-analytics' && typeof window !== 'undefined' && 'gtag' in window) {
    window.gtag('event', eventName, properties);
  }
}

/**
 * Handle performance metrics for analytics
 */
export const webVitalsHandler: ReportHandler = (metric) => {
  if (!analyticsConfig.enabled) return;

  const { name, value, id } = metric;
  
  // Report web vitals metrics as events
  trackEvent('web_vitals', {
    metric_name: name,
    metric_value: Math.round(name === 'CLS' ? value * 1000 : value),
    metric_id: id,
  });
};

/**
 * Set up error tracking
 */
function setupErrorTracking(): void {
  const originalOnError = window.onerror;
  
  window.onerror = function(message, source, lineno, colno, error) {
    // Call the original handler if it exists
    if (originalOnError) {
      originalOnError.apply(this, [message, source, lineno, colno, error]);
    }
    
    // Track the error
    trackEvent('error', {
      message: message,
      source: source,
      line: lineno,
      column: colno,
      stack: error?.stack,
      url: window.location.href
    });
    
    return false; // Allow default error handling
  };
}

// Add to window global so it can be called from anywhere
if (typeof window !== 'undefined') {
  // Initialize the saalari object with analytics property to fix the type error
  window.saalari = {
    analytics: {
      trackEvent,
      trackPageView
    }
  };
}

// Declare the global window extension
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    saalari: {
      analytics: {
        trackEvent: typeof trackEvent;
        trackPageView: typeof trackPageView;
      }
    }
  }
}
